import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from './useRedux';
import { useLocation } from 'react-router-dom';

import { useSlice } from './useSlice';
import { selectMenu } from '../features/infoSlice';

import { Library, Options } from '../types/library';
import { libApiSlice } from '../services/library';
import { shareApiSlice } from '../services/share';
import { driveApiSlice } from '../services/drive';
import { searchApiSlice } from '../services/search';
import { trashApiSlice } from '../services/trash';

import { usePage } from './usePage';
import { isOfType } from '../utils/typeGuard';

export default function useList() {
  const dispatch = useAppDispatch();
  const menu = useAppSelector(selectMenu);
  const location = useLocation();

  const { pageChanged } = usePage();

  const [getLibList, { isSuccess: isLibSuccess }] =
    libApiSlice.useLazyGetLibraryQuery();
  const [getSharedLibList, { isSuccess: isSharedLibSuccess }] =
    shareApiSlice.useLazyGetSharedLibraryQuery();
  const [getDriveList, { isSuccess: isDriveSuccess }] =
    driveApiSlice.useLazyGetResourcesQuery();
  const [getPublicList, { isSuccess: isPublicSuccess }] =
    searchApiSlice.useLazyGetPublicListQuery();
  const [getTrashList, { isSuccess: isTrashSuccess }] =
    trashApiSlice.useLazyGetTrashListQuery();

  const { selectOptions, selectAll } = useSlice();
  const { sortType, sortOrder, isShowShared }: Options =
    useAppSelector(selectOptions);

  const folderId = useMemo(
    () => location?.state?.folderId,
    [location?.state?.folderId]
  );

  const isSuccess = useMemo(() => {
    switch (menu) {
      case 'library':
        return isLibSuccess;
      case 'share':
        return isSharedLibSuccess;
      case 'mydrive':
        return isDriveSuccess;
      case 'search':
        return isPublicSuccess;
      case 'trash':
        return isTrashSuccess;
      default:
        return false;
    }
  }, [
    menu,
    isLibSuccess,
    isSharedLibSuccess,
    isDriveSuccess,
    isPublicSuccess,
    isTrashSuccess,
  ]);

  let list = useAppSelector(selectAll);

  if (menu === 'mydrive' && !isShowShared) {
    list = list.filter((item) => !isOfType(item, 'share') || !item.share);
  }

  useEffect(() => {
    dispatch(pageChanged(0));

    try {
      switch (menu) {
        case 'library':
          getLibList({
            page: 0,
            sortBy: sortType,
            sortDir: sortOrder,
          }).unwrap();
          break;
        case 'share':
          getSharedLibList({
            page: 0,
            sortBy: sortType,
            sortDir: sortOrder,
          }).unwrap();
          break;
        case 'mydrive':
          getDriveList({
            ...(folderId && { folderId }),
            page: 0,
            sortBy: sortType,
            sortDir: sortOrder,
          }).unwrap();
          break;
        case 'search':
          getPublicList({
            page: 0,
            sortBy: sortType,
            sortDir: sortOrder,
          }).unwrap();
          break;
        case 'trash':
          getTrashList({
            page: 0,
            sortBy: sortType,
            sortDir: sortOrder,
          }).unwrap();
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('Failed to get list', error);
    }
  }, [sortType, sortOrder, folderId]);

  return { list, isSuccess };
}
